import { createPath } from 'history';
import { parse } from 'qs';
import { createElement, FC, Fragment, useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router';

import { BusinessRegionAdRegionKey, OrganizationDetailFragment } from '../../generated/graphql';
import mergeUrlQuery from '../../utility/mergeUrlQuery';
import TransitionToKaizenIdModal from '../component/compound/TransitionToKaizenIdModal';
import { context as ConfigContext } from '../context/config';
import { context as DependencyContext } from '../context/dependency';
import { context as HistoryContext } from '../context/history';
import { context as StateContext } from '../context/state';
import Page from '../page/DirectionTypesPage';
import { useClearRecoilState } from '../page/ProjectEditingPage/hooks/useClearRecoilState';
import useUpdateProject from '../page/ProjectEditingPage/hooks/useUpdateProject';

type Props = {
  className?: string;
};

const DirectionTypesPage: FC<Props> = ({ className }) => {
  const {
    language,
    location,
    lastAccessedOrganization,
    authentication,
    fatalError,
    availableOrganizations,
  } = useContext(StateContext);
  const history = useContext(HistoryContext);
  const {
    authenticationUsecase,
    listProjectTypesUsecase,
    organizationUsecase,
    projectDesignInspirationUsecase,
  } = useContext(DependencyContext);
  const config = useContext(ConfigContext);
  const clearRecoilState = useClearRecoilState();
  const { createDraftProject } = useUpdateProject();

  const query = parse(location.search.slice(1));
  const organizationId: string = query.org || null;

  const region: BusinessRegionAdRegionKey =
    lastAccessedOrganization != null && lastAccessedOrganization.businessRegion.key
      ? lastAccessedOrganization.businessRegion.key
      : 'jp';

  useEffect(() => {
    authenticationUsecase.needSignedIn();
    if (organizationId) {
      organizationUsecase.refreshPointBalance(organizationId);
      listProjectTypesUsecase.prepareProjectTypes();
    }
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // 新規プロジェクト作成時のRegion別処理を実行
  const [isModalOpen, setModal] = useState(false);

  const handleClickNewProjectButton = () => {
    const organization = lastAccessedOrganization;
    const user = authentication.signedInUser;

    // Recoil内の編集中のプロジェクトステートを飛ばし、サラの状態でプロジェクト編集開始できるようにする
    clearRecoilState();

    if (organization) {
      const region = organization.businessRegion.key;

      switch (region) {
        case 'jp':
          createDraftProject({
            organizationId: organization.id,
            projectTypeKey: 'project_type_jp_for_ad',
          });

          // SMB向けUI Epic3以降に開放
          // history.push('/design_inspirations');
          break;
        case 'us':
          projectDesignInspirationUsecase.openDesignInspirationScreen();
          projectDesignInspirationUsecase.prepare({
            projectTypeKey: 'project_type_us_for_ad',
          });
          break;
        case 'gl':
          createDraftProject({
            organizationId: organization.id,
            projectTypeKey: 'project_type_gl_for_ad',
          });
          break;
        default:
          break;
      }
    } else if (user) {
      // Organizationが存在しない場合、モーダルを表示してIDへの組織登録を促す
      // Organization.BusinessRegionを参照できないため、Langを参照する
      // https://kaizen.qiita.com/yuta-yamamoto/items/7d1a72e3d01ec3c5ec8f
      setModal(true);
    }
  };

  if (!organizationId) {
    let organization!: OrganizationDetailFragment;

    if (lastAccessedOrganization) {
      organization = lastAccessedOrganization;
    } else if (availableOrganizations.items.length >= 1) {
      organization = availableOrganizations.items[0];
    }

    if (organization) {
      return <Redirect to={createPath(mergeUrlQuery(history.location, { org: organization.id }))} />;
    }
  } else {
    if (!availableOrganizations.items.some((item) => item.id === organizationId)) {
      return <Redirect to="/direction_types" />;
    }
  }

  return !fatalError && authentication.fetching === 0 && authentication.signedInUser ? (
    <>
      <Page
        onClickAdType={() => {
          handleClickNewProjectButton();
        }}
        onClickOtherType={() => {
          history.push('/project_types');
        }}
        className={className}
        region={region}
      />

      {authentication.signedInUser ? (
        <TransitionToKaizenIdModal
          urlToId={config.ID_WEB_URL}
          open={isModalOpen}
          language={language}
          onRequestClose={() => setModal(false)}
        />
      ) : null}
    </>
  ) : null;
};

export default DirectionTypesPage;
